import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
} from '@mui/material';
import { useSellerGroups } from '../../../lib/query/hooks';
import { downloadCSV } from '../../../lib/utils';
import type { SimpleAccount } from '@/lib/types';

interface SellerGroupListProps {
  account: SimpleAccount;
}

export function SellerGroupList({ account }: SellerGroupListProps) {
  const { data: sellerGroups = [], isLoading: loading, error } = useSellerGroups(account.code);

  // CSVダウンロード処理
  const handleDownloadCSV = () => {
    const csvData = sellerGroups.map((sellerGroup) => ({
      店舗グループカスタマーID: sellerGroup.customer_id,
      店舗グループ名: sellerGroup.name,
    }));

    const filename = `店舗グループ一覧_${account.code}_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvData, filename);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>店舗グループ一覧を読み込み中...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error instanceof Error ? error.message : '店舗グループ一覧の読み込みに失敗しました。'}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardContent>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="h6">店舗グループ一覧 ({sellerGroups.length}件)</Typography>
            {sellerGroups.length > 0 && (
              <Button variant="outlined" onClick={handleDownloadCSV} size="small">
                CSVでダウンロード
              </Button>
            )}
          </Box>

          {sellerGroups.length === 0 ? (
            <Alert severity="info">店舗グループデータが見つかりませんでした。</Alert>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                mt: 2,
                overflowX: 'auto',
                maxWidth: '100%',
                '& .MuiTable-root': {
                  minWidth: 650,
                },
              }}
            >
              <Table sx={{ whiteSpace: 'nowrap' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>店舗グループカスタマーID</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>店舗グループ名</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>サイトタイプ</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>表示状態</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sellerGroups.map((sellerGroup, index) => (
                    <TableRow key={`${sellerGroup.id}-${index}`}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{sellerGroup.customer_id}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{sellerGroup.name}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {sellerGroup.site_type || '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Chip
                          label={sellerGroup.is_enabled_current ? '有効' : '無効'}
                          color={sellerGroup.is_enabled_current ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
